import { cn } from '@/lib/utils'
import { ScrollReveal } from '@/components/motion'
import OrbitingCirclesGlobe, { type Orbit } from '@/components/ui/orbiting-circles-02'
import { OfficeAppIcon, type OfficeApp } from '@/components/landing/OfficeAppIcon'

// AI agent definitions: colour, people avatar and role.
// Avatars are DiceBear "Notionists" by Zoish (CC0 1.0), generated once and
// stored in public/images/agents.
export const agents = [
  {
    name: 'Alex',
    avatar: '/images/agents/alex.svg',
    role: 'Content Finder',
    color: '#3B82F6', // Blue
    description: 'Searches your libraries and past documents to surface the right clause, figure or asset in seconds.',
  },
  {
    name: 'Scarlet',
    avatar: '/images/agents/scarlet.svg',
    role: 'Brand Protector',
    color: '#EF4444', // Red
    description: 'Checks every draft against your brand rules (fonts, colours, logos and tone) before it goes out.',
  },
  {
    name: 'Sia',
    avatar: '/images/agents/sia.svg',
    role: 'Writing Partner',
    color: '#8B5CF6', // Purple
    description: 'Rewrites and polishes copy so every document reads clearly in your house voice.',
  },
  {
    name: 'Olga',
    avatar: '/images/agents/olga.svg',
    role: 'Content Strategist',
    color: '#F59E0B', // Amber
    description: 'Suggests what to include, based on what has worked for similar clients and proposals.',
  },
  {
    name: 'Dimi',
    avatar: '/images/agents/dimi.svg',
    role: 'Document Builder',
    color: '#F97316', // Orange
    description: 'Assembles complete first drafts from your templates and the client details you give it.',
  },
  {
    name: 'Andrew',
    avatar: '/images/agents/andrew.svg',
    role: 'Content Analyst',
    color: '#14B8A6', // Teal
    description: 'Tracks which documents and sections perform, so every next draft starts stronger.',
  },
]

const officeApps: Array<{ app: OfficeApp; name: string; purpose: string }> = [
  {
    app: 'word',
    name: 'Word',
    purpose: 'Word add-in: build proposals, contracts and letters from your templates, with brand-safe fields.',
  },
  {
    app: 'excel',
    name: 'Excel',
    purpose: 'Calculation sheets, pricing models and data tables, generated from your logic and kept on brand.',
  },
  {
    app: 'powerpoint',
    name: 'PowerPoint',
    purpose: 'Slides and decks assembled from approved layouts, charts and imagery.',
  },
  {
    app: 'outlook',
    name: 'Outlook',
    purpose: 'Emails, signatures and reply templates, consistent across the whole team.',
  },
]

function TooltipBody({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <>
      <span className="block font-[family-name:var(--font-landing-display)] text-[11px] uppercase leading-snug">
        {title}
      </span>
      <span className="mt-1 block text-[13px] leading-snug opacity-85">{children}</span>
    </>
  )
}

const nodeLabel =
  'pointer-events-none absolute left-1/2 top-full mt-2 -translate-x-1/2 whitespace-nowrap text-center'

// Inner ring: the agents. Six nodes 30deg apart, mirrored, so all six are
// always on the visible half.
const agentOrbit: Orbit = {
  size: 'w-[27rem] h-[27rem] md:w-[46rem] md:h-[46rem]',
  duration: 48,
  mirror: true,
  nodes: agents.map((agent, i) => ({
    key: agent.name,
    angle: -75 + i * 30,
    tooltip: <TooltipBody title={`${agent.name} · ${agent.role}`}>{agent.description}</TooltipBody>,
    node: (
      <div className="group">
        <div
          className="h-12 w-12 overflow-hidden transition-transform duration-200 group-hover:scale-110 md:h-16 md:w-16"
          style={{ backgroundColor: agent.color, boxShadow: `0 10px 26px ${agent.color}45` }}
        >
          <img src={agent.avatar} alt="" draggable={false} className="h-full w-full translate-y-[6%] scale-110 object-cover" />
        </div>
        <div className={nodeLabel}>
          <span className="block text-xs md:text-sm font-bold" style={{ color: agent.color }}>
            {agent.name}
          </span>
          <span className="hidden md:block text-[11px] text-[var(--color-landing-gray-600)] dark:text-[var(--color-landing-gray-400)]">
            {agent.role}
          </span>
        </div>
      </div>
    ),
  })),
}

// Outer ring: the Office apps the agents work inside. Four nodes 45deg
// apart, mirrored, rotating the other way.
const officeOrbit: Orbit = {
  size: 'w-[40rem] h-[40rem] md:w-[68rem] md:h-[68rem]',
  duration: 72,
  mirror: true,
  nodes: officeApps.map((office, i) => ({
    key: office.app,
    angle: -67.5 + i * 45,
    tooltip: <TooltipBody title={office.name}>{office.purpose}</TooltipBody>,
    node: (
      <div className="group">
        <div className="flex h-12 w-12 md:h-16 md:w-16 items-center justify-center border border-[var(--color-landing-gray-200)] bg-white shadow-[0_8px_24px_rgba(0,0,0,0.08)] transition-transform duration-200 group-hover:scale-110 dark:border-[var(--color-landing-gray-700)] dark:bg-[#12121a]">
          <OfficeAppIcon app={office.app} className="h-8 w-8 md:h-11 md:w-11" />
        </div>
        <span className={cn(nodeLabel, 'text-[11px] md:text-xs font-semibold text-[var(--color-landing-gray-700)] dark:text-[var(--color-landing-gray-300)]')}>
          {office.name}
        </span>
      </div>
    ),
  })),
}

/**
 * The name and line, still and centred inside the turning globe: the far
 * particles pass behind it, the near ones in front. A light halo in the
 * section colour keeps it legible.
 */
function SphereLabel() {
  return (
    <p
      className="flex flex-col items-center gap-1 whitespace-nowrap text-center font-[family-name:var(--font-landing-display)] uppercase leading-[1.25] [--halo:var(--color-landing-gray-50)] dark:[--halo:#12121a]"
      style={{ textShadow: '0 0 3px var(--halo), 0 0 8px var(--halo)' }}
    >
      <span className="text-[1.5rem] text-[var(--color-landing-accent)] md:text-[2.5rem] dark:text-[#8a84ff]">Templatean</span>
      <span className="text-[0.75rem] text-[var(--color-landing-gray-900)] md:text-[1.05rem] dark:text-white">Your templates &amp; brand</span>
    </p>
  )
}

interface AITeamOrbitProps {
  className?: string
  /** Content under the globe, inside the same section. */
  children?: React.ReactNode
}

/**
 * Templatean at the centre, the agents on the inner ring and the Office
 * apps they work inside on the outer ring: a half-dome orbit rising from a
 * particle globe (OrbitingCirclesGlobe).
 */
export function AITeamOrbit({ className, children }: AITeamOrbitProps) {
  return (
    <section className={cn('relative pt-20 md:pt-32 overflow-hidden', className)}>
      <div className="landing-container relative z-10">
        <ScrollReveal className="text-center mb-10 md:mb-14">
          <p className="landing-text-overline text-[var(--color-landing-gray-600)] dark:text-[var(--color-landing-gray-400)] mb-4">
            Pre-Built Agents to Get You Started
          </p>
          <h2 className="landing-text-display-lg text-[var(--color-landing-gray-900)] dark:text-white max-w-3xl mx-auto">
            Start with Our Agents—Or Build Your Own
          </h2>
        </ScrollReveal>
      </div>

      <OrbitingCirclesGlobe
        orbits={[agentOrbit, officeOrbit]}
        className="h-[25rem] md:h-[38rem]"
        globeSize="w-72 md:w-[34rem]"
        center={<SphereLabel />}
      />
      {children}
    </section>
  )
}

export default AITeamOrbit
